/**
 * WorkOrderPartiesPanel — role-based parties on a work order (epic #763, #768)
 *
 * Lists every party role with its current person/company, and lets staff
 * set/replace each role (orderer is required and cannot be cleared). Replaces
 * the implicit "customer" with explicit parties.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { useWorkOrderParties, useRemoveWorkOrderParty, type WorkOrderParty } from '@/hooks/useWorkOrderParties';
import {
  WORK_ORDER_PARTY_ROLES,
  isRequiredPartyRole,
  type WorkOrderPartyRole,
} from '@/lib/workOrderPartyRoles';
import { WorkOrderPartyDialog } from './WorkOrderPartyDialog';
import { useNotify } from '@/stores/notificationStore';
import { describeApiError } from '@/lib/describeApiError';

interface WorkOrderPartiesPanelProps {
  workOrderId: string;
}

export function WorkOrderPartiesPanel({ workOrderId }: WorkOrderPartiesPanelProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const { data, isLoading } = useWorkOrderParties(workOrderId);
  const removeParty = useRemoveWorkOrderParty();
  const [editRole, setEditRole] = useState<WorkOrderPartyRole | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const byRole = new Map<string, WorkOrderParty>();
  for (const party of data?.data ?? []) {
    byRole.set(party.role, party);
  }

  const handleRemove = async (role: WorkOrderPartyRole) => {
    try {
      await removeParty.mutateAsync({ workOrderId, role });
      notify.success(t('parties.removed'), '');
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('parties.removeFailed') }));
    }
  };

  return (
    <>
      <List disablePadding>
        {WORK_ORDER_PARTY_ROLES.map((role) => {
          const party = byRole.get(role);
          const required = isRequiredPartyRole(role);
          return (
            <ListItem
              key={role}
              divider
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={tc('edit')}>
                    <IconButton edge="end" aria-label={tc('edit')} onClick={() => setEditRole(role)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {party && !required && (
                    <Tooltip title={tc('delete')}>
                      <span>
                        <IconButton
                          edge="end"
                          aria-label={tc('delete')}
                          onClick={() => void handleRemove(role)}
                          disabled={removeParty.isPending}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t(`parties.roles.${role}`)}
                    </Typography>
                    {required && <Chip size="small" label={t('parties.required')} />}
                  </Stack>
                }
                secondary={
                  party ? (
                    <>
                      <Typography variant="body1" component="span" sx={{ display: 'block' }}>
                        {party.displayName ?? '—'}
                        {party.businessId ? ` (${party.businessId})` : ''}
                      </Typography>
                      {(party.phone || party.email) && (
                        // The pickup person's number is the one to call when the car is ready (#3401).
                        <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
                          {party.phone && (
                            <Link
                              href={`tel:${party.phone}`}
                              underline="hover"
                              aria-label={t('parties.callPhone', { phone: party.phone })}
                            >
                              {party.phone}
                            </Link>
                          )}
                          {party.phone && party.email ? ' · ' : ''}
                          {party.email}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" component="span" color="text.disabled">
                      {t('parties.notSet')}
                    </Typography>
                  )
                }
              />
            </ListItem>
          );
        })}
      </List>

      {editRole && (
        <WorkOrderPartyDialog
          open={!!editRole}
          workOrderId={workOrderId}
          role={editRole}
          onClose={() => setEditRole(null)}
        />
      )}
    </>
  );
}
